import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mayaSiViewRoute } from '../../../basics/constants/routes.constants';
import sortFilterOptions from '../../../basics/options/sotrFilter.options';
import useTherapeuticAreaOptions from '../../../basics/options/therapeuticArea.options';
import { ProductsContext } from '../../../contexts/products';
import { SortFilter } from '../../../generated/maya.types';
import DateTimeDisplay from '../../../yoda-ui/components/DateTimeDisplay/DateTimeDisplay';
import GridListAction from 'components/GridListAction/GridListAction';

const useStrategicImperativeListConfig = (
  editSiCallback: (siId: string) => void,
  duplicateSiCallback: (siId: string) => void,
  addChildAction: (siId: string) => void,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const flags = useFlags();

  const { allProducts } = useContext(ProductsContext);

  const siTitleFieldConfig = {
    name: 'name',
    placeholder: t('maya_si_form_si_title_placeholder'),
    defaultValue: '',
    required: false,
  };

  const siTherapeuticAreaFieldConfig = {
    name: 'therapeuticArea',
    options: useTherapeuticAreaOptions(allProducts),
    placeholder: t('maya_si_form_si_therapeutic_area_placeholder'),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const siSortFieldConfig = {
    name: 'sort',
    options: sortFilterOptions,
    defaultValue: SortFilter.NEWEST,
    required: false,
    disableRenderTags: true,
  };

  const strategicImperativeListColumnConfig: GridColDef[] = [
    {
      field: 'title',
      headerName: t('maya_strategic_imperative_list_title_header_label'),
      flex: 0.6,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'therapeuticArea',
      headerName: t('maya_strategic_imperative_list_TA_header_label'),
      flex: 0.1,
      sortable: false,
    },
    {
      field: 'updated',
      type: 'string',
      headerName: t('maya_strategic_imperative_list_last_update_header_label'),
      renderCell: (cellValues: GridRenderCellParams) => {
        const data = new Date(cellValues.value as string);
        return (
          <DateTimeDisplay
            date={ data.toLocaleDateString('en-GB') }
            time={ data.toLocaleTimeString('uk-UA', { hour12: false }) }
          />
        );
      },
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'action',
      headerName: '',
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params: GridRenderCellParams) => {
        const actions = [
          {
            label: t('maya_strategic_imperative_quick_view'),
            callback: () => {
              navigate(`${mayaSiViewRoute}?id=${params.row._id}`);
            },
          },
          {
            label: t('maya_strategic_imperative_list_add_child_customer_initiative'),
            callback: () => {
              addChildAction(params.row._id);
            },
          },
        ];

        if (flags.mayaBiogenlinc4493EnableStrategicImperativeManagement) {
          actions.unshift({
            label: t('actions_edit_label'),
            callback: () => { editSiCallback(params.row._id); },
          },
          {
            label: t('actions_duplicate_label'),
            callback: () => { duplicateSiCallback(params.row._id); },
          });
        }

        return (
          <GridListAction
            actions={ actions }
          />
        );
      },
      align: 'right',
      headerAlign: 'right',
      flex: 0.1,
    },
  ];

  return {
    strategicImperativeListColumnConfig,
    siTitleFieldConfig,
    siTherapeuticAreaFieldConfig,
    siSortFieldConfig,
  };
};

export default useStrategicImperativeListConfig;

